import React, { useEffect, useState } from 'react'
import debounce from 'debounce-promise'
import { Button, Card, Input, SelectAsync, LoadingFull } from '@arnsbogroup/react-voicearchive'
import { useIntl } from 'react-intl'
import MemoryRequestActions from 'components/MemoryRequestActions'
import messages from './messages'

const loadLanguageOptions = debounce((query) =>
  fetch(`/rest/v2/language?search[name]=${encodeURIComponent(query)}&pageSize=50`)
    .then(r => r.json())
    .then(res => {
    const list = Array.isArray(res) ? res : (res.data || [])
      return list
      .filter(l => l.l18n_iso === 'en')
      .map(l => ({ value: String(l.uid), label: l.name }))
    })
, 300)

const loadCustomerOptions = debounce(async (query) => {
  const results = new Map()

  const nameRes = await fetch(`/rest/v2/customer?search[name]=${encodeURIComponent(query)}&pageSize=15`)
    .then(r => r.json())
  ;(nameRes.data || []).forEach(c => results.set(c.uid, { value: c.uid, label: `${c.name} (#${c.uid})` }))

  if (/^\d+$/.test(query)) {
    try {
      const orderRes = await fetch(`/rest/v2/order/${query}`).then(r => r.json())
      const orderData = orderRes.data
      if (orderData?.customer_uid) {
        const custRes = await fetch(`/rest/v2/customer/${orderData.customer_uid}`).then(r => r.json())
        const c = custRes.data
        if (c) results.set(c.uid, { value: c.uid, label: `${c.name} (#${c.uid}) — via order #${query}` })
      }
    } catch (_) {}
  }

  return Array.from(results.values())
}, 300)

const loadOrderOptions = debounce((query) =>
  fetch(`/rest/v2/order?query=${encodeURIComponent(query)}&pageSize=15&orderBy=uid&orderDirection=DESC`)
    .then(r => r.json())
    .then(({ data }) => (data || []).map(o => ({ value: o.uid, label: `${o.name} (#${o.uid})` })))
, 300)

const loadTaskOptions = debounce((query) =>
  fetch(`/rest/v2/task?id=${encodeURIComponent(query)}&pageSize=15&orderBy=uid&orderDirection=DESC`)
    .then(r => r.json())
    .then(({ data }) => (data || []).map(t => ({
      value: t.uid,
      label: `#${t.uid}${t.title ? ' — ' + t.title : ''}`,
    })))
, 300)

const resolveLanguageTag = (tag) =>
  fetch(`/rest/v2/language?pageSize=300`)
    .then(r => r.json())
    .then(res => {
      const list = Array.isArray(res) ? res : (res.data || [])
      const match = list.find(l => String(l.uid) === String(tag))
      return match ? { value: String(match.uid), label: match.name } : { value: tag, label: tag }
    })

const ENTITY_CONFIG = {
  order: { url: id => `/rest/v2/order/${id}`, parse: d => d, label: d => ({ value: d.uid, label: `${d.name} (#${d.uid})` }) },
  ordertask: { url: id => `/rest/v2/task/${id}`, parse: ({ data }) => data, label: d => ({ value: d.uid, label: `#${d.uid}${d.name ? ' — ' + d.name : ''}` }) },
  customer: { url: id => `/rest/v1/customer/${id}`, parse: d => d, label: d => ({ value: d.uid, label: `${d.name} (#${d.uid})` }) },
}

const resolveOptions = (type, ids) => {
  const { url, parse, label } = ENTITY_CONFIG[type]
  return Promise.all(
    ids.map(id =>
      fetch(url(id))
        .then(r => r.json())
        .then(d => { const parsed = parse(d); return parsed ? label(parsed) : null })
    )
  ).then(results => results.filter(Boolean))
}

const MemoryDetails = ({
  resourceId = null,
  initialOrderId = null,
  initialTaskId = null,
  initialCustomerId = null,
  initialMemory = null,
  onSave = () => {},
  onClose = null,
}) => {
  const { formatMessage } = useIntl()

  const [customers, setCustomers] = useState([])
  const [orders, setOrders] = useState([])
  const [tasks, setTasks] = useState([])

  // Edit mode: single text/language
  const [text, setText] = useState('')
  const [language, setLanguage] = useState(null)

  // Create mode: multiple entries
  const [entries, setEntries] = useState([{ id: 0, text: '', language: null }])

  const [saving, setSaving] = useState(false)
  const [initialLoading, setInitialLoading] = useState(
    !!(resourceId || initialOrderId || initialTaskId || initialCustomerId || initialMemory)
  )

  useEffect(() => {
    if (resourceId) {
      loadMemory()
    } else {
      resolveInitialValues()
    }
  }, [resourceId])

  const loadMemory = async () => {
    setInitialLoading(true)
    try {
      const { success, data } = await fetch(`/rest/v2/memory/${resourceId}?includeRelations=1`).then(r => r.json())
      if (!success || !data) return

      setText(data.text || '')

      if (data.language_uid) {
        setLanguage(await resolveLanguageTag(String(data.language_uid)))
      }

      const links = data.links || []
      const orderLinks = links.filter(l => l.link_type === 'order')
      const taskLinks = links.filter(l => l.link_type === 'ordertask')
      const customerLinks = links.filter(l => l.link_type === 'customer')

      const [resolvedOrders, resolvedTasks, resolvedCustomers] = await Promise.all([
        resolveOptions('order', orderLinks.map(l => l.link_uid)),
        resolveOptions('ordertask', taskLinks.map(l => l.link_uid)),
        resolveOptions('customer', customerLinks.map(l => l.link_uid)),
      ])

      setOrders(resolvedOrders)
      setTasks(resolvedTasks)
      setCustomers(resolvedCustomers)
    } catch (_) {
      toastr.error(formatMessage(messages.loadError))
    } finally {
      setInitialLoading(false)
    }
  }

  const resolveInitialValues = async () => {
    if (!initialOrderId && !initialTaskId && !initialCustomerId && !initialMemory) {
      setInitialLoading(false)
      return
    }
    try {
      let orderIds = initialOrderId ? [initialOrderId] : []
      let taskIds = initialTaskId ? [initialTaskId] : []
      let customerIds = initialCustomerId ? [initialCustomerId] : []

      if (initialMemory) {
        const links = initialMemory.links || []
        links.forEach(l => {
          if (l.link_type === 'order') orderIds = [...new Set([...orderIds, l.link_uid])]
          else if (l.link_type === 'ordertask') taskIds = [...new Set([...taskIds, l.link_uid])]
          else if (l.link_type === 'customer') customerIds = [...new Set([...customerIds, l.link_uid])]
        })
        const lang = initialMemory.language_uid
          ? { value: String(initialMemory.language_uid), label: initialMemory.language_name || String(initialMemory.language_uid) }
          : null
        setEntries([{ id: 0, text: initialMemory.text || '', language: lang }])
      }

      const [orders, tasks, customers] = await Promise.all([
        resolveOptions('order', orderIds),
        resolveOptions('ordertask', taskIds),
        resolveOptions('customer', customerIds),
      ])
      if (orders.length) setOrders(orders)
      if (tasks.length) setTasks(tasks)
      if (customers.length) setCustomers(customers)
    } catch (_) {}
    finally {
      setInitialLoading(false)
    }
  }

  const buildLinks = () => [
    ...customers.map(c => ({ linkType: 'customer', linkUid: c.value })),
    ...orders.map(o => ({ linkType: 'order', linkUid: o.value })),
    ...tasks.map(t => ({ linkType: 'ordertask', linkUid: t.value })),
  ]

  const updateEntry = (id, field, value) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e))
  }

  const addEntry = () => {
    setEntries(prev => [...prev, { id: Date.now(), text: '', language: null }])
  }

  const removeEntry = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  const handleSubmit = async () => {
    const links = buildLinks()
    if (!links.length) {
      return
    }

    setSaving(true)
    try {
      if (resourceId) {
        if (!text) {
          return
        }
        const { success, errors } = await fetch(`/rest/v2/memory/${resourceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, languageUid: language?.value ? Number(language.value) : null, links }),
        }).then(r => r.json())
        if (!success) {
          toastr.error((errors || []).join(', '))
          return
        }
        toastr.success(formatMessage(messages.saveChanges))
        onSave(resourceId)
      } else {
        const validEntries = entries.filter(e => e.text.trim())
        if (!validEntries.length) {
          return
        }
        let firstUid = null
        for (const entry of validEntries) {
          const { success, data, errors } = await fetch('/rest/v2/memory', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: entry.text, languageUid: entry.language?.value ? Number(entry.language.value) : null, links }),
          }).then(r => r.json())
          if (!success) {
            toastr.error((errors || []).join(', '))
            return
          }
          if (!firstUid) {
            firstUid = data.uid
          }
        }
        onSave(firstUid)
      }
    } catch (e) {
      toastr.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const links = buildLinks()
  const canSubmit = resourceId
    ? !!text && links.length > 0
    : entries.some(e => e.text.trim()) && links.length > 0

  const validEntryCount = entries.filter(e => e.text.trim()).length

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-400 text-sm">
        {formatMessage(messages.loading)}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold text-sm text-gray-700">{formatMessage(messages.linksHeading)}</h3>
        <SelectAsync
          label={formatMessage(messages.customerLabel)}
          isMulti
          value={customers}
          onChange={val => setCustomers(val || [])}
          loadOptions={loadCustomerOptions}
          defaultOptions
          cacheOptions
          placeholder={formatMessage(messages.customerPlaceholder)}
          menuPortalTarget={document.body}
        />
        <SelectAsync
          label={formatMessage(messages.orderLabel)}
          isMulti
          value={orders}
          onChange={val => setOrders(val || [])}
          loadOptions={loadOrderOptions}
          defaultOptions
          cacheOptions
          placeholder={formatMessage(messages.orderPlaceholder)}
          menuPortalTarget={document.body}
        />
        <SelectAsync
          label={formatMessage(messages.taskLabel)}
          isMulti
          value={tasks}
          onChange={val => setTasks(val || [])}
          loadOptions={loadTaskOptions}
          defaultOptions
          cacheOptions
          placeholder={formatMessage(messages.taskPlaceholder)}
          menuPortalTarget={document.body}
        />
      </Card>

      {resourceId ? (
        <>
          <Card className="p-4 space-y-2">
            <h3 className="font-semibold text-sm text-gray-700">{formatMessage(messages.textValueHeading)}</h3>
            <Input
              label={formatMessage(messages.memoryTextLabel)}
              value={text}
              onChange={({ target }) => setText(target.value)}
              placeholder={formatMessage(messages.memoryTextPlaceholder)}
            />
          </Card>

          <Card className="p-4 space-y-2">
            <h3 className="font-semibold text-sm text-gray-700">{formatMessage(messages.languageHeading)}</h3>
            <SelectAsync
              label={formatMessage(messages.languageLabel)}
              value={language}
              onChange={setLanguage}
              loadOptions={loadLanguageOptions}
              defaultOptions
              cacheOptions
              placeholder={formatMessage(messages.languagePlaceholder)}
              isClearable
              menuPortalTarget={document.body}
            />
          </Card>
        </>
      ) : (
        <>
          {entries.map((entry, index) => (
            <Card key={entry.id} className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">
                  {entries.length > 1
                    ? formatMessage(messages.memoryIndex, { index: index + 1 })
                    : formatMessage(messages.textValueHeading)}
                </h3>
                {entries.length > 1 && (
                  <Button
                    variant="text"
                    state="negative"
                    size="xs"
                    onClick={() => removeEntry(entry.id)}
                  >
                    {formatMessage(messages.removeButton)}
                  </Button>
                )}
              </div>
              <Input
                label={formatMessage(messages.memoryTextLabel)}
                value={entry.text}
                onChange={({ target }) => updateEntry(entry.id, 'text', target.value)}
                placeholder={formatMessage(messages.memoryTextPlaceholder)}
              />
              <SelectAsync
                label={formatMessage(messages.languageLabel)}
                value={entry.language}
                onChange={(val) => updateEntry(entry.id, 'language', val)}
                loadOptions={loadLanguageOptions}
                defaultOptions
                cacheOptions
                placeholder={formatMessage(messages.languagePlaceholder)}
                isClearable
                menuPortalTarget={document.body}
              />
            </Card>
          ))}

          <Button
            variant="outline"
            state="secondary"
            size="sm"
            onClick={addEntry}
          >
            {formatMessage(messages.addAnotherMemory)}
          </Button>
        </>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button
          state="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving
            ? formatMessage(messages.savingButton)
            : resourceId
              ? formatMessage(messages.saveChanges)
              : validEntryCount > 1
                ? formatMessage(messages.createMemories, { count: validEntryCount })
                : formatMessage(messages.createMemory)}
        </Button>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            {formatMessage(messages.cancel)}
          </Button>
        )}
      </div>

      {resourceId && (
        <div className="pt-4 border-t border-gray-100">
          <MemoryRequestActions memoryIds={[resourceId]} />
        </div>
      )}
    </div>
  )
}

export default MemoryDetails
